import { HttpHeaders } from '@angular/common/http';

/**
 * Errores HTTP con su status y su mensaje reales.
 *
 * El camino de siempre los pierde: el `ErrorInterceptor` deja intactos sólo
 * 400/422/429 y el resto lo relanza como string (`message` del back o
 * `statusText`), y el `ApiRestInterceptor`, que va por fuera, convierte ese
 * string en un 401 sintético sin cuerpo (`statusText: 'Sesión no disponible'`).
 * El componente recibe entonces un 401 que nunca existió: ni el 409 de "la
 * tasa cambió", ni el 403 de permisos, ni el 5xx del envío incierto.
 *
 * El arreglo no toca los interceptores globales. Las llamadas que necesitan el
 * error real van con `X-Skip-Error-Interceptor` (el escape que ya usan
 * `others-payouts`, `peru-payouts`, `precio-binance`…) y lo traducen acá. La
 * traducción acepta las tres formas que pueden llegar: `HttpErrorResponse`
 * real, string aplanado y el 401 sintético.
 */

/** Cabecera que hace que el `ErrorInterceptor` deje pasar el error intacto. */
export const CABECERA_SIN_INTERCEPTOR = 'X-Skip-Error-Interceptor';

/** Los mismos headers con el escape del `ErrorInterceptor`. */
export function sinInterceptorGlobal(headers: HttpHeaders): HttpHeaders {
  return headers.set(CABECERA_SIN_INTERCEPTOR, 'true');
}

/**
 * Textos que no son del API sino del protocolo: el `statusText` que pone el
 * `ErrorInterceptor` cuando el back no mandó `message`, el cuerpo en inglés
 * de API Gateway o de Nest sin mensaje propio. No sirven para el cliente: se
 * cambian por el texto propio de cada pantalla.
 */
export const TEXTO_HTTP_GENERICO =
  /^(unknown error|internal server error|internal server error\.|bad gateway|service unavailable|gateway timeout|endpoint request timed out|forbidden|not found|conflict|unauthorized|too many requests|bad request|ok|http failure.*|sesión no disponible|no autenticado)$/i;

/**
 * El 500 del catch genérico de los controllers del wallet (`Error procesando
 * la compra de USDT con VES.`, `Error procesando el retiro VES→CLP.`, `Error
 * procesando el swap de USDT a VES.`…): un error inesperado, no un rechazo de
 * negocio. El resultado de la operación es incierto.
 */
export const ERROR_GENERICO_CONTROLLER = /^error procesando\b/i;

/** `statusText` del 401 que fabrica el `ApiRestInterceptor` al recibir un error aplanado. */
const STATUS_TEXT_APLANADO = 'Sesión no disponible';

/** 401 real del back (o sin sesión): la sesión venció. */
export const TEXTO_SESION_VENCIDA = 'Tu sesión venció, vuelve a iniciar sesión.';

/**
 * El `DataUpdateBlockInterceptor` cortó la operación ANTES de enviarla (datos
 * del cliente en revisión). Mismo texto que su aviso.
 */
export const TEXTO_BLOQUEO_REVISION =
  'Tu información está en revisión. Te avisaremos cuando puedas volver a operar.';

export interface OpcionesErrorHttpReal {
  /**
   * La llamada salió con `X-Skip-Error-Interceptor`. Entonces el
   * `ErrorInterceptor` no aplanó nada, y el 401 sintético «Sesión no
   * disponible» sólo puede venir de un error que no es HTTP dentro de la
   * cadena: el bloqueo 423 del `DataUpdateBlockInterceptor` (o no poder
   * leer la sesión). En los dos casos la petición NO salió: no es incierto.
   */
  conEscape?: boolean;
  /**
   * La llamada MUEVE DINERO (débito, swap, retiro, transacción). Ahí todo 5xx
   * es incierto aunque traiga mensaje: Nest responde 500 con el
   * `exception.message` crudo de cualquier error que no sea HttpException
   * (un timeout de SQS después del débito, por ejemplo), y ese texto no
   * prueba que la operación no se hizo. Los editores del panel no lo usan:
   * ahí un 5xx con mensaje se muestra y se puede reintentar.
   */
  mueveDinero?: boolean;
}

/**
 * 5xx cuyo mensaje dice que los saldos YA se revirtieron (hoy:
 * `retiro-usdt-ves`, "Error al procesar el retiro. Los saldos han sido
 * revertidos automáticamente."). No es incierto: no quedó nada hecho, se
 * muestra tal cual y se puede reintentar.
 */
export const REVERSA_EXPLICITA = /revertid/i;

/** `getHeaders()` de los servicios lanza esto cuando no hay sesión: la petición no salió. */
const SIN_TOKEN = /no se pudo obtener el token/i;

export interface ErrorHttpReal {
  /** Status HTTP; `undefined` si se perdió en el camino (error aplanado). */
  status: number | undefined;
  /** El `message` del back, o `fallback` si no hay uno que sirva al cliente. */
  message: string;
  detalles: any;
  /**
   * El resultado es incierto (la operación pudo haberse hecho): sin conexión
   * (status 0), timeout, o 5xx / error aplanado SIN mensaje propio del API.
   * Un 4xx o un mensaje real del servidor no lo es: se muestra y se puede
   * reintentar.
   */
  transitorio: boolean;
  /**
   * El `code` del cuerpo, cuando el back lo manda (hoy: `RED_CRIPTO_INACTIVA`
   * en el 422 de una red cripto pausada). Sin él la pantalla tendría que
   * adivinar el motivo por el texto. Solo aparece si vino.
   */
  codigo?: string;
}

function unirMensaje(crudo: any): string {
  if (Array.isArray(crudo)) return crudo.map(String).join(' ').trim();
  return typeof crudo === 'string' ? crudo.trim() : '';
}

/** Sin mensaje propio del API: vacío, texto de protocolo o el 500 genérico del controller. */
function sinMensajeDelServidor(texto: string): boolean {
  return !texto || TEXTO_HTTP_GENERICO.test(texto) || ERROR_GENERICO_CONTROLLER.test(texto);
}

/**
 * Traduce cualquier error de una llamada HTTP a `{ status, message, detalles,
 * transitorio }`. `fallback` es el texto de la pantalla cuando el back no
 * mandó un mensaje que sirva.
 */
export function errorHttpReal(error: any, fallback: string, opciones: OpcionesErrorHttpReal = {}): ErrorHttpReal {
  // Aplanado a string por el `ErrorInterceptor` (llamada sin el escape).
  if (typeof error === 'string') {
    const texto = error.trim();
    const generico = !texto || TEXTO_HTTP_GENERICO.test(texto);
    return {
      status: undefined,
      message: !generico ? texto : fallback,
      detalles: null,
      transitorio: sinMensajeDelServidor(texto),
    };
  }

  // Sin token: `getHeaders()` cortó antes de enviar. Sesión vencida, no incierto.
  if (error instanceof Error && SIN_TOKEN.test(error.message)) {
    return { status: 401, message: TEXTO_SESION_VENCIDA, detalles: null, transitorio: false };
  }

  const status: number | undefined = typeof error?.status === 'number' ? error.status : undefined;

  // Bloqueo del `DataUpdateBlockInterceptor` (objeto plano `{ status: 423 }`
  // o un 423 del back): no se envió nada.
  if (status === 423) {
    return { status: 423, message: TEXTO_BLOQUEO_REVISION, detalles: null, transitorio: false };
  }

  if (status === 401 && error?.error == null && error?.statusText === STATUS_TEXT_APLANADO) {
    // Con el escape: el 423 del bloqueo (u otro error no HTTP) que el
    // `ApiRestInterceptor` convirtió en 401. La petición no salió.
    if (opciones.conEscape) {
      return { status: 423, message: TEXTO_BLOQUEO_REVISION, detalles: null, transitorio: false };
    }
    // Sin el escape: un error ya aplanado por el `ErrorInterceptor`; el
    // status real y el mensaje se perdieron. Puede haber sido un 5xx.
    return { status: undefined, message: fallback, detalles: null, transitorio: true };
  }

  // 401 real del back (guard de Cognito) o sin sesión («No autenticado»): la
  // sesión venció. El guard corta antes de operar, así que no es incierto.
  if (status === 401) {
    return { status: 401, message: TEXTO_SESION_VENCIDA, detalles: null, transitorio: false };
  }

  const cuerpo = error?.error;
  // Sólo `message` (o `title`, que es lo único que mandan los 400 de la
  // lambda `transaccion`) del JSON: un cuerpo string suele ser el HTML de un
  // 502 del balanceador, que no es un mensaje para el cliente.
  const texto = unirMensaje(cuerpo?.message) || unirMensaje(cuerpo?.title);
  const generico = !texto || TEXTO_HTTP_GENERICO.test(texto);
  const es5xx = typeof status === 'number' && status >= 500 && status <= 599;
  const codigo = typeof cuerpo?.code === 'string' && cuerpo.code.trim() ? cuerpo.code.trim() : null;
  return {
    status,
    message: !generico ? texto : fallback,
    detalles: cuerpo?.detalles || null,
    transitorio:
      status === 0 ||
      (es5xx && !REVERSA_EXPLICITA.test(texto) && (opciones.mueveDinero || sinMensajeDelServidor(texto))),
    ...(codigo ? { codigo } : {}),
  };
}

/**
 * ¿El back rechazó la operación porque la tasa enviada ya no vale? No se creó
 * nada: hay que volver a cotizar. VES→CLP responde 409 ("La tasa cambió o no
 * es válida…"); el swap USDT→VES, 400 ("La tasa cambió mientras
 * confirmabas…", con o sin voseo); la lambda `transaccion`, 400 con `title`
 * "Tasas de cambio actualizadas".
 */
export function esRechazoPorTasa(status: number | undefined, mensaje: unknown): boolean {
  const m = String(mensaje ?? '');
  if (status === 409) return /tasa/i.test(m);
  if (status === 400) return /la tasa cambi|tasas de cambio actualizadas/i.test(m);
  return false;
}
